import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Home,
  MessageCircle,
  Send,
  Users,
  CalendarDays,
  Play,
  Pause,
  Check,
  Mail,
  ShieldCheck,
  FileText,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const STEP_MS = 3200;

const STEPS = ["Convocation LRE", "Feuille de présence", "Vote en direct", "Procès-verbal"];
const NAV = [Home, MessageCircle, Send, Users, CalendarDays];

const Avatar = ({ initials, color }) => (
  <span
    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-ink ${color}`}
  >
    {initials}
  </span>
);

const MiniShell = ({ active, children }) => (
  <div className="flex h-full bg-white text-left">
    <div className="flex w-10 shrink-0 flex-col items-center gap-2.5 bg-[linear-gradient(180deg,#2BC97E,#00C4CC)] pt-4">
      {NAV.map((Icon, i) => (
        <span
          key={i}
          className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            i === active ? "bg-white text-electric" : "text-white/70"
          }`}
        >
          <Icon size={12} />
        </span>
      ))}
    </div>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const StepConvocation = () => (
  <MiniShell active={4}>
    <div className="p-4">
      <p className="text-[12px] font-bold text-ink">Assemblées générales</p>
      <p className="text-[8px] text-ink/40">Convocations en LRE</p>
      <div className="mt-3 rounded-xl border border-black/5 bg-mist p-3.5">
        <p className="text-[8px] font-bold uppercase tracking-wider text-gold">
          12 Février 2020
        </p>
        <p className="mt-0.5 text-[12px] font-semibold text-ink">
          AG Annuelle — Visioconférence à 360°
        </p>
        <div className="mt-2.5 flex items-center gap-2 rounded-lg border border-black/5 bg-white p-2 shadow-sm">
          <Mail size={11} className="shrink-0 text-electric" />
          <span className="flex-1 text-[9px] font-semibold text-ink/70">
            Convocation_AG_Annuelle.pdf
          </span>
          <span className="rounded-full bg-electric/10 px-2 py-0.5 text-[8px] font-bold text-electric">
            LRE
          </span>
        </div>
        <div className="mt-2.5 flex items-center -space-x-1.5">
          <Avatar initials="JD" color="bg-[#BFE3F7] ring-2 ring-white" />
          <Avatar initials="MC" color="bg-[#F5E960] ring-2 ring-white" />
          <Avatar initials="FD" color="bg-[#D9C9F5] ring-2 ring-white" />
          <span className="flex h-5 items-center rounded-full bg-electric px-1.5 text-[7px] font-bold text-white">
            +12
          </span>
        </div>
        <p className="mt-2 text-[8px] text-ink/40">
          Accusés de réception suivis pour chaque copropriétaire.
        </p>
      </div>
    </div>
  </MiniShell>
);

const StepPresence = () => (
  <MiniShell active={4}>
    <div className="p-4">
      <p className="text-[12px] font-bold text-ink">Feuille de présence</p>
      <p className="text-[8px] text-ink/40">Digitalisée, en temps réel</p>
      <div className="mt-3 space-y-1.5">
        {[
          { initials: "JD", color: "bg-[#BFE3F7]", name: "John Doe", role: "Présent" },
          { initials: "MC", color: "bg-[#F5E960]", name: "Marc Dubois", role: "Présent" },
          { initials: "FD", color: "bg-[#D9C9F5]", name: "Fête des voisins", role: "Visio" },
        ].map((p, i) => (
          <div
            key={p.name}
            className="flex items-center gap-2 rounded-lg border border-black/5 bg-white p-2 shadow-sm"
          >
            <Avatar initials={p.initials} color={p.color} />
            <span className="flex-1 text-[9px] font-semibold text-ink/80">{p.name}</span>
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 + i * 0.35, duration: 0.3, ease: EASE }}
              className="flex items-center gap-1 rounded-full bg-electric/10 px-2 py-0.5 text-[8px] font-bold text-electric"
            >
              <Check size={8} />
              {p.role}
            </motion.span>
          </div>
        ))}
        <p className="pt-1 text-[8px] text-ink/40">
          Quorum calculé automatiquement au fur et à mesure des arrivées.
        </p>
      </div>
    </div>
  </MiniShell>
);

const StepVote = () => (
  <MiniShell active={4}>
    <div className="p-4">
      <p className="text-[12px] font-bold text-ink">Vote en direct</p>
      <p className="text-[8px] text-ink/40">Résolution à l'ordre du jour</p>
      <div className="mt-3 rounded-xl border border-black/5 bg-mist p-3.5">
        <p className="text-[11px] font-semibold text-ink">Approbation des comptes</p>
        <div className="mt-3 space-y-2.5">
          {[
            { label: "Pour", width: "68%", bar: "bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]" },
            { label: "Contre", width: "22%", bar: "bg-ink/15" },
          ].map((r) => (
            <div key={r.label}>
              <div className="flex items-center justify-between text-[9px] font-semibold text-ink/60">
                <span>{r.label}</span>
                <span>{r.width}</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-white">
                <motion.div
                  initial={{ width: "0%" }}
                  animate={{ width: r.width }}
                  transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
                  className={`h-full rounded-full ${r.bar}`}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-[8px] font-semibold text-electric">
          <ShieldCheck size={10} />
          Vote électronique à distance, sécurisé et tracé
        </p>
      </div>
    </div>
  </MiniShell>
);

const StepPV = () => (
  <MiniShell active={4}>
    <div className="p-4">
      <p className="text-[12px] font-bold text-ink">Procès-verbal</p>
      <p className="text-[8px] text-ink/40">Généré à la clôture de l'AG</p>
      <div className="mt-3 rounded-xl border border-black/5 bg-mist p-3.5">
        <div className="flex items-center gap-2.5 rounded-lg border border-black/5 bg-white p-2.5 shadow-sm">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-electric/10 text-electric">
            <FileText size={14} />
          </span>
          <span className="flex-1">
            <span className="block text-[10px] font-semibold text-ink">
              PV — Assemblée Générale
            </span>
            <span className="block text-[8px] text-ink/40">
              Généré automatiquement, prêt à signer
            </span>
          </span>
        </div>
        <div className="mt-2.5 space-y-1.5">
          <p className="flex items-center gap-1.5 text-[9px] font-semibold text-electric">
            <Check size={10} />
            Envoyé à tous les copropriétaires en LRE
          </p>
          <p className="flex items-center gap-1.5 text-[9px] font-semibold text-electric">
            <Check size={10} />
            Archivé conformément au module RGPD &amp; Archivage
          </p>
        </div>
      </div>
    </div>
  </MiniShell>
);

const SCREENS = [StepConvocation, StepPresence, StepVote, StepPV];

export const DemoPlayer = () => {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), STEP_MS);
    return () => clearTimeout(t);
  }, [step, playing]);

  const Screen = SCREENS[step];

  return (
    <div
      className="overflow-hidden rounded-2xl bg-white shadow-[0_35px_80px_-25px_rgba(0,55,70,0.55)]"
      data-testid="demo-player"
      onMouseEnter={() => setPlaying(false)}
      onMouseLeave={() => setPlaying(true)}
    >
      {/* Barre de navigateur */}
      <div className="flex items-center gap-1.5 border-b border-black/5 bg-white px-3.5 py-2">
        <span className="h-2 w-2 rounded-full bg-ink/15" />
        <span className="h-2 w-2 rounded-full bg-ink/15" />
        <span className="h-2 w-2 rounded-full bg-ink/15" />
        <span className="ml-2 flex-1 truncate rounded-full bg-mist px-3 py-1 text-[8px] font-medium text-ink/40">
          app.syndicappli.fr — Assemblées générales
        </span>
        <button
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Mettre en pause la démo" : "Lire la démo"}
          data-testid="demo-play-pause"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-white transition-transform duration-200 hover:scale-110"
        >
          {playing ? <Pause size={11} /> : <Play size={11} className="ml-px" />}
        </button>
      </div>

      {/* Segments de progression */}
      <div className="flex gap-2 px-3.5 pt-2.5">
        {STEPS.map((label, i) => (
          <button key={label} onClick={() => setStep(i)} className="flex-1 text-left" data-testid={`demo-step-${i}`}>
            <div className="h-1 overflow-hidden rounded-full bg-ink/10">
              <div
                key={`${step}-${playing}`}
                className="h-full rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]"
                style={
                  i < step
                    ? { width: "100%" }
                    : i === step
                      ? {
                          width: "100%",
                          animation: "demo-fill 3200ms linear forwards",
                          animationPlayState: playing ? "running" : "paused",
                        }
                      : { width: "0%" }
                }
              />
            </div>
            <span
              className={`mt-1 block text-[8px] font-bold uppercase tracking-wider ${
                i === step ? "text-electric" : "text-ink/35"
              }`}
            >
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* Écran animé */}
      <div className="relative h-[290px] sm:h-[320px]">
        <AnimatePresence initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="absolute inset-0"
            data-testid={`demo-screen-${step}`}
          >
            <Screen />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
